/**
 * @fileoverview Reactive hooks for consuming dataset rift queries.
 *
 * The id-deriving hooks (`useBlockRift`, `useBlockChartRift`, `useDatasetRift`)
 * read current observable state (datasets, blocks, board filters) via Legend
 * State and compute the rift `riftId` plus a {@link RiftDescriptor} that they
 * register so any downstream consumer holding only the id can build the query.
 *
 * The data-consuming hooks (`useRiftQuery`, `useRiftStat`) run TanStack Query
 * against that descriptor. `placeholderData: keepPreviousData` provides the
 * stale-while-revalidate behaviour the old hand-rolled store implemented with a
 * fallback lookup; observer-counted `gcTime` provides eviction.
 */

import { useValue } from "@legendapp/state/react";
import type { DataType } from "@repo/lanes";
import type {
  ColumnsConfig,
  OperationType,
  RiftConfig,
} from "@repo/shared/rift";
import { mergeColumnsConfigs } from "@repo/shared/utils/merge-columns-configs";
import { mergeRiftConfigs } from "@repo/shared/utils/merge-rift-configs";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { blocks$ } from "../blocks/blocks.crud";
import { getBoardFiltersAsFilterTypes } from "../board-filters/board-filters.helpers";
import { getActiveDatasetRunId } from "../datasets/datasets.actions";
import { datasets$, datasetsLocal$ } from "../datasets/datasets.crud";
import { buildChartRiftSql, buildRiftSql } from "./dataset-rifts.actions";
import { getVersionedRiftId } from "./dataset-rifts.helpers";
import {
  type RiftDescriptor,
  registerRiftDescriptor,
  riftQueryOptions,
  riftStatQueryOptions,
} from "./dataset-rifts.query";

/** Builds a `name -> DataType` map from the dataset's column metadata. */
const buildColumnDefs = (
  datasetId: string,
): Record<string, DataType> | null => {
  const columns = datasets$[datasetId].columns.get();
  if (!columns) return null;
  return Object.fromEntries(
    Object.entries(columns).map(([name, col]) => [name, col.type]),
  );
};

/**
 * Result for a block rift: the computed id and the merged riftConfig that
 * generated its SQL. Returns `null` for the id when the dataset is not
 * published or all columns are hidden — consumers render their empty state.
 */
type BlockRiftResult = {
  riftId: string | null;
  mergedRiftConfig: RiftConfig | null;
};

/**
 * Computes the block's merged riftConfig and columnsConfig along with the
 * resolved board filters. Shared by `useBlockRift` and `useBlockChartRift`.
 * Returns `null` when the dataset is not published or every column is hidden.
 */
const computeBlockRiftContext = (
  blockId: string,
): {
  datasetId: string;
  mergedRiftConfig: RiftConfig | null;
  columnsConfig: ColumnsConfig;
  boardFilters: ReturnType<typeof getBoardFiltersAsFilterTypes> | null;
} | null => {
  const datasetId = blocks$[blockId].datasetId.get();
  if (!datasetId) return null;

  const runId = getActiveDatasetRunId(datasetId);
  if (!runId) return null;

  const datasetColumns = datasets$[datasetId].columns.get();
  const datasetColumnsConfig = datasets$[datasetId].columnsConfig.get();
  const blockColumnsConfig = blocks$[blockId].columnsConfig.get();
  const columnsConfig = mergeColumnsConfigs(
    datasetColumnsConfig,
    blockColumnsConfig,
  );

  const hiddenColumns = Object.entries(columnsConfig)
    .filter(([, cfg]) => cfg.visible === false)
    .map(([name]) => name);

  if (datasetColumns) {
    const totalColumns = Object.keys(datasetColumns).length;
    let hiddenInDataset = 0;
    for (const name of hiddenColumns) {
      if (name in datasetColumns) hiddenInDataset++;
    }
    if (totalColumns > 0 && hiddenInDataset >= totalColumns) return null;
  }

  const blockRiftConfig = blocks$[blockId].riftConfig.get();
  const datasetRiftConfig = datasets$[datasetId].riftConfig.get();
  // Apply the merged hiddenColumns to the local riftConfig so the SQL hides
  // the right columns. We must clone because the observable's `get()` returns
  // a snapshot we shouldn't mutate.
  const localRiftConfig = blockRiftConfig
    ? { ...blockRiftConfig, hiddenColumns }
    : null;
  const mergedRiftConfig = mergeRiftConfigs(datasetRiftConfig, localRiftConfig);

  const boardId = blocks$[blockId].boardId.get();
  const boardFilters = boardId
    ? getBoardFiltersAsFilterTypes(boardId, datasetId)
    : null;

  return { datasetId, mergedRiftConfig, columnsConfig, boardFilters };
};

/**
 * Reactively computes the block's main `riftId` and registers its query
 * descriptor. Re-runs whenever any input changes (datasets, blocks, board
 * filters). Use in components that then read status/data via `useRiftQuery`.
 */
export const useBlockRift = (blockId: string): BlockRiftResult => {
  const result = useValue<{
    riftId: string | null;
    mergedRiftConfig: RiftConfig | null;
    descriptor: RiftDescriptor | null;
  }>(() => {
    const ctx = computeBlockRiftContext(blockId);
    if (!ctx) return { riftId: null, mergedRiftConfig: null, descriptor: null };

    const runId = getActiveDatasetRunId(ctx.datasetId) ?? "";
    const sql = buildRiftSql({
      datasetId: ctx.datasetId,
      runId,
      riftConfig: ctx.mergedRiftConfig,
      boardFilters: ctx.boardFilters,
      columnsConfig: ctx.columnsConfig,
    });
    const riftId = getVersionedRiftId(sql, runId);
    const descriptor: RiftDescriptor = {
      riftId,
      datasetId: ctx.datasetId,
      runId,
      sql,
      riftConfig: ctx.mergedRiftConfig,
      boardFilters: ctx.boardFilters,
      columnsConfig: ctx.columnsConfig,
      columnDefs: buildColumnDefs(ctx.datasetId),
    };
    return { riftId, mergedRiftConfig: ctx.mergedRiftConfig, descriptor };
  });

  if (result.descriptor) registerRiftDescriptor(result.descriptor);

  return { riftId: result.riftId, mergedRiftConfig: result.mergedRiftConfig };
};

/**
 * Reactively computes the block's chart `chartRiftId` and registers its query
 * descriptor. Returns `null` when the block isn't a graph, has no graphConfig,
 * the dataset isn't published, or all columns are hidden.
 */
export const useBlockChartRift = (blockId: string): string | null => {
  const result = useValue<{
    riftId: string | null;
    descriptor: RiftDescriptor | null;
  }>(() => {
    const displayType = blocks$[blockId].displayType.get();
    const graphConfig = blocks$[blockId].graphConfig.get();
    if (displayType !== "GRAPH" || !graphConfig) {
      return { riftId: null, descriptor: null };
    }

    const ctx = computeBlockRiftContext(blockId);
    if (!ctx) return { riftId: null, descriptor: null };

    const runId = getActiveDatasetRunId(ctx.datasetId) ?? "";
    const sql = buildChartRiftSql({
      datasetId: ctx.datasetId,
      runId,
      riftConfig: ctx.mergedRiftConfig,
      boardFilters: ctx.boardFilters,
      graphConfig,
      columnsConfig: ctx.columnsConfig,
    });
    const riftId = getVersionedRiftId(sql, runId);
    const descriptor: RiftDescriptor = {
      riftId,
      datasetId: ctx.datasetId,
      runId,
      sql,
      riftConfig: ctx.mergedRiftConfig,
      boardFilters: ctx.boardFilters,
      columnsConfig: ctx.columnsConfig,
      columnDefs: buildColumnDefs(ctx.datasetId),
    };
    return { riftId, descriptor };
  });

  if (result.descriptor) registerRiftDescriptor(result.descriptor);

  return result.riftId;
};

/**
 * Result for a dataset rift: the computed id (or `null` while etag is
 * missing) on the dataset's own draft/preview page.
 */
type DatasetRiftResult = {
  riftId: string | null;
};

/**
 * Reactively computes the dataset page's own `riftId` from its riftConfig and
 * columnsConfig, and registers its query descriptor so the `RiftTable` in the
 * bottom pane can run the query.
 *
 * Prefers the client-only draft-preview run (`datasetsLocal$[id].draftRunId`,
 * seeded by `runDataset`) when present so the editor previews the freshly
 * executed draft query; otherwise renders the dataset's published run.
 */
export const useDatasetRift = (datasetId: string): DatasetRiftResult => {
  const result = useValue<{
    riftId: string | null;
    descriptor: RiftDescriptor | null;
  }>(() => {
    if (!datasetId) return { riftId: null, descriptor: null };

    const draftRunId = datasetsLocal$[datasetId].draftRunId.get();
    const runId = draftRunId ?? getActiveDatasetRunId(datasetId) ?? "";
    if (!runId) return { riftId: null, descriptor: null };

    // Draft-preview runs carry their own column metadata (the schema may differ
    // from the published run); published runs use the denormalized columns.
    const columns = draftRunId
      ? (datasetsLocal$[datasetId].draftRunColumns.get() ?? null)
      : (datasets$[datasetId].columns.get() ?? null);

    const riftConfig = datasets$[datasetId].riftConfig.get();
    const columnsConfig = datasets$[datasetId].columnsConfig.get();
    const hiddenColumns = Object.entries(columnsConfig ?? {})
      .filter(([, cfg]) => cfg.visible === false)
      .map(([name]) => name);
    const localRiftConfig = riftConfig
      ? { ...riftConfig, hiddenColumns }
      : null;

    const sql = buildRiftSql({
      datasetId,
      runId,
      riftConfig: localRiftConfig,
      boardFilters: null,
      columnsConfig,
      columns,
    });
    const riftId = getVersionedRiftId(sql, runId);
    const columnDefs = columns
      ? Object.fromEntries(
          Object.entries(columns).map(([name, col]) => [name, col.type]),
        )
      : null;
    const descriptor: RiftDescriptor = {
      riftId,
      datasetId,
      runId,
      sql,
      riftConfig: localRiftConfig,
      boardFilters: null,
      columnsConfig: columnsConfig ?? null,
      columnDefs,
    };
    return { riftId, descriptor };
  });

  if (result.descriptor) registerRiftDescriptor(result.descriptor);

  return { riftId: result.riftId };
};

/**
 * Runs (and subscribes to) the main rift result query for a `riftId`.
 * `keepPreviousData` renders the previously-loaded result while a new key
 * (e.g. after an etag republish or filter change) loads.
 */
export const useRiftQuery = (riftId: string | null) =>
  useQuery({
    ...riftQueryOptions(riftId),
    placeholderData: keepPreviousData,
  });

/** Runs (and subscribes to) a single stat aggregate query. */
export const useRiftStat = (
  riftId: string | null,
  column: string,
  operation: OperationType | null | undefined,
  opts?: { previous?: boolean; enabled?: boolean },
) =>
  useQuery({
    ...riftStatQueryOptions(riftId, column, operation, opts),
    placeholderData: keepPreviousData,
  });
